import React from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n";
import { IMAGES } from "@/assets";

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.35 } },
};
const lineChild = {
  hidden: { y: "115%" },
  show: { y: 0, transition: { duration: 0.95, ease: [0.22, 1, 0.36, 1] } },
};
const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: 0.9 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Line = ({ children, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span variants={lineChild} className={`block ${className}`}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLang();
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 900], [0, 180]);
  const imgScale = useTransform(scrollY, [0, 900], [1.05, 1.18]);
  const overlayOpacity = useTransform(scrollY, [0, 600], [1, 1.15]);

  return (
    <section data-testid="hero-section" className="relative min-h-screen overflow-hidden">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img
          src={IMAGES.heroModel}
          alt="Lumina Africa haute couture"
          className="h-full w-full object-cover object-top"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/45 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/85 via-transparent to-transparent" />
      </motion.div>
      <div className="grain" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 min-h-screen flex items-center">
        <div className="max-w-2xl pt-28 pb-20">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="flex items-center gap-3 text-[0.7rem] sm:text-xs uppercase tracking-[0.3em] text-[#d4af37]"
            data-testid="hero-kicker"
          >
            <motion.span
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              style={{ transformOrigin: "left" }}
              className="h-px w-8 bg-[#d4af37]"
            />
            {t.hero.kicker}
          </motion.p>

          <motion.h1
            variants={lineParent}
            initial="hidden"
            animate="show"
            data-testid="hero-title"
            className="font-serif-lux font-light leading-[1.02] sm:leading-[0.98] text-4xl sm:text-6xl lg:text-7xl mt-6 text-white"
          >
            <Line>{t.hero.titleA}</Line>
            <Line>{t.hero.titleB}</Line>
            <Line className="italic gold-shimmer">{t.hero.titleAccent}</Line>
          </motion.h1>

          <motion.p
            custom={0}
            variants={fade}
            initial="hidden"
            animate="show"
            data-testid="hero-subtitle"
            className="mt-8 max-w-xl text-base lg:text-lg font-light leading-relaxed text-white/70"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            custom={1}
            variants={fade}
            initial="hidden"
            animate="show"
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/register"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-2 bg-[#d4af37] text-black font-medium px-8 py-4 hover:bg-[#f3e5ab] transition-colors duration-300"
            >
              {t.hero.ctaPrimary}
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/catalogue"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center border border-white/30 text-white font-medium px-8 py-4 hover:border-white hover:bg-white/5 transition-all duration-300"
            >
              {t.hero.ctaSecondary}
            </Link>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/40"
      >
        <span className="text-[0.6rem] tracking-[0.3em] uppercase">Scroll</span>
        <span className="h-10 w-px bg-gradient-to-b from-[#d4af37] to-transparent" />
      </motion.div>
    </section>
  );
};
